/*
 * luda-pricing.js — single source of truth for cart pricing math.
 *
 * Pure functions only. No DOM access. No HTML composition.
 * Consumers gather their own items, call pricing(), then render however they want.
 *
 * Constants mirror the rules Shopify will actually charge:
 *   - 10% subscription discount on subtotal (applied before volume)
 *   - $10 volume discount per complete box of 10 packs (post-M3)
 *   - Free ground shipping at >=8 packs per ORDER (ground zone; per-box
 *     rule retired 2026-08-31 — one box per order)
 *
 * Constants overridable via applyConfig(cfg) using the response from
 * /api/shipping-rate/config (already proven pattern in BAB + portal).
 *
 * Self-test runs on load. Failures console.error loudly so regressions
 * surface immediately on any page that includes the snippet.
 */
(function () {
  'use strict';

  var M = {
    SUB_DISCOUNT_RATE: 0.10,
    VOLUME_PER_BOX: 10,
    PACKS_PER_BOX: 10,
    FREE_SHIP_PER_BOX: 8,
    MAX_PACKS: 30,
    MIN_PACKS: 3,
    AIR_DISCOUNT_PER_BOX: 20,
    // Volume ladder cap (2026-10-01, Eugene): ONE tier only — $10 at 10+
    // packs; the 20/30 PACK automatics are off. /config volume_max_tiers
    // can override (3 = the old $10/$20/$30 ladder).
    VOLUME_MAX_TIERS: 1,
    SELLING_PLANS: { 4: '692509540718', 6: '692509573486', 8: '692509606254' },
    VERSION: '1.2.0'
  };

  M.applyConfig = function (cfg) {
    if (!cfg) return;
    if (cfg.packs_per_box) M.PACKS_PER_BOX = cfg.packs_per_box;
    if (cfg.free_ship_threshold) M.FREE_SHIP_PER_BOX = cfg.free_ship_threshold;
    if (cfg.max_packs) M.MAX_PACKS = cfg.max_packs;
    if (cfg.air_discount_per_box) M.AIR_DISCOUNT_PER_BOX = cfg.air_discount_per_box;
    if (cfg.volume_per_box != null) M.VOLUME_PER_BOX = cfg.volume_per_box;
    if (cfg.sub_discount_rate != null) M.SUB_DISCOUNT_RATE = cfg.sub_discount_rate;
    if (cfg.volume_max_tiers != null) M.VOLUME_MAX_TIERS = cfg.volume_max_tiers;
  };

  M.fmt = function (n) {
    return '$' + (Number(n) || 0).toFixed(2);
  };

  /**
   * Pure pricing math.
   *   items: Array<{qty: number, price: number}>
   *   opts:  {isSubscription?: bool, frequency?: number}  (frequency reserved for v2)
   * Returns: {totalPacks, subtotal, subDiscount, volumeDiscount,
   *           oneTimeTotal, subTotal, total, savings}
   *
   * Stacking order: sub % first, then volume flat amount. Matches
   * Shopify checkout. Don't change without re-reading
   * docs/volume-discount-renewal-gap.md.
   */
  M.pricing = function (items, opts) {
    items = items || [];
    opts = opts || {};
    var totalPacks = 0;
    var subtotal = 0;
    for (var i = 0; i < items.length; i++) {
      var qty = Number(items[i].qty) || 0;
      var price = Number(items[i].price) || 0;
      totalPacks += qty;
      subtotal += qty * price;
    }
    // Stacking matches Shopify post-M3 architecture (2026-05-16 LATE):
    // - Selling plan no longer applies per-line sub discount (pricingPolicies
    //   stripped from the 3 BAB/QS plans).
    // - "PACK DISCOUNT" automatic discount applies $10/$20/$30 off post-line-
    //   subtotal as an order-level discount.
    // - "Subscribe & Save" automatic discount applies 10% off the POST-VOLUME
    //   amount as an order-level discount.
    // Net: customer pays (subtotal − volume) × 0.9 on sub orders. Verified
    // against Shopify's checkout breakdown — page total === charged amount.
    var volumeDiscount = Math.min(Math.floor(totalPacks / M.PACKS_PER_BOX), M.VOLUME_MAX_TIERS) * M.VOLUME_PER_BOX;
    var oneTimeTotal = subtotal - volumeDiscount;
    var subDiscount = oneTimeTotal * M.SUB_DISCOUNT_RATE;
    var subTotal = oneTimeTotal - subDiscount;
    var isSub = !!opts.isSubscription;
    var total = isSub ? subTotal : oneTimeTotal;
    return {
      totalPacks: totalPacks,
      subtotal: subtotal,
      subDiscount: subDiscount,
      volumeDiscount: volumeDiscount,
      oneTimeTotal: oneTimeTotal,
      subTotal: subTotal,
      total: total,
      savings: oneTimeTotal - subTotal
    };
  };

  /**
   * Local-derive FREE shipping qualification. Mirrors the shipping API so
   * callers never trust a cached est.total_shipping === 0
   * (the 2026-05-04 stale-cache fix invariant).
   *
   * PER ORDER since 2026-08-31 (one box per order — Grip packs a single
   * larger box; the per-box split is retired server-side too): qualifies
   * when zone is ground AND the ORDER has >= FREE_SHIP_PER_BOX packs.
   * The constant keeps its name because the /config key
   * (free_ship_threshold) is a contract; PACKS_PER_BOX remains the
   * VOLUME-DISCOUNT unit only.
   */
  M.freeShipQualifies = function (zone, totalPacks) {
    return zone === 'ground' && totalPacks >= M.FREE_SHIP_PER_BOX;
  };

  // Self-test — runs once on load. Failures are loud, success is silent.
  (function selfTest() {
    try {
      console.assert(M.pricing([], {}).total === 0, 'empty cart total === 0');

      // Matches Shopify post-M3 architecture: sub = 10% × (subtotal − volume),
      // so subTotal = (subtotal − volume) × 0.9 = oneTimeTotal × 0.9
      var t10sub = M.pricing([{ qty: 10, price: 11.99 }], { isSubscription: true });
      var t10postVolume = 119.90 - M.VOLUME_PER_BOX;
      var expected10subTotal = t10postVolume * (1 - M.SUB_DISCOUNT_RATE);
      var expected10subDiscount = t10postVolume * M.SUB_DISCOUNT_RATE;
      console.assert(Math.abs(t10sub.subTotal - expected10subTotal) < 0.01, '10-pack sub subTotal (got ' + t10sub.subTotal + ', expected ' + expected10subTotal + ')');
      console.assert(Math.abs(t10sub.subDiscount - expected10subDiscount) < 0.01, '10-pack subDiscount (got ' + t10sub.subDiscount + ', expected ' + expected10subDiscount + ')');
      console.assert(t10sub.volumeDiscount === M.VOLUME_PER_BOX, '10-pack volumeDiscount: $' + M.VOLUME_PER_BOX + ' (got ' + t10sub.volumeDiscount + ')');

      var t20ot = M.pricing([{ qty: 20, price: 11.99 }], { isSubscription: false });
      // One volume tier since 2026-10-01: 20 packs still gets just $10.
      var expected20vol = Math.min(2, M.VOLUME_MAX_TIERS) * M.VOLUME_PER_BOX;
      var expected20otTotal = 239.80 - expected20vol;
      console.assert(Math.abs(t20ot.oneTimeTotal - expected20otTotal) < 0.01, '20-pack one-time (got ' + t20ot.oneTimeTotal + ', expected ' + expected20otTotal + ')');
      console.assert(t20ot.volumeDiscount === expected20vol, '20-pack volumeDiscount: $' + expected20vol + ' (got ' + t20ot.volumeDiscount + ')');

      console.assert(M.freeShipQualifies('ground', 8) === true, '8-pack ground qualifies');
      console.assert(M.freeShipQualifies('ground', 7) === false, '7-pack ground does not qualify');
      console.assert(M.freeShipQualifies('air', 10) === false, 'air never qualifies');
      console.assert(M.freeShipQualifies('ground', 17) === true, '17-pack qualifies (per-ORDER rule, 2026-08-31)');
      console.assert(M.freeShipQualifies('ground', 12) === true, '12-pack qualifies (old per-box rule charged the tail)');
      console.assert(M.freeShipQualifies('unknown', 10) === false, 'unknown zone never qualifies');
    } catch (e) {
      console.error('LudasPricing self-test FAILED:', e);
    }
  })();

  window.LudasPricing = M;
})();
